import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  HostListener,
  Inject, OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { CommunicationsProvider } from '../../providers/communications-provider';

import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { CurrentUser } from '../../../common/providers/current-user';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { concat, from, Observable, of, Subscription } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { map, mergeMap, publishReplay, switchMap, tap } from 'rxjs/operators';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CommunicationMessageDialogComponent } from '../../components/message-dialog/message-dialog.component';
import { Organization } from "../../../common/models/organization";
import { Athlete } from "../../../common/models/athlete";
import { User } from "../../../common/models/user";
import { ApiBaseService } from "../../../common/providers/api-base-service";
import { HasPendingChangesGuard } from "../../../common/guards/has-pending-changes";
import { OrganizationRole } from "../../../common/models/organization-role";
import { MatIcon } from "@angular/material/icon";
import { MatMenuItem, MatMenuModule } from "@angular/material/menu";
import { MatIconButton } from "@angular/material/button";
import { DateClosestPipe } from "../../../common/pipes/date-closest";
import { LowerCasePipe, NgForOf, NgIf } from "@angular/common";
import { EmojifyPipe } from "../../../chat/pipe/emojify.pipe";
import { MatTooltip } from "@angular/material/tooltip";
import { MatFormField } from "@angular/material/form-field";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { MatInput } from "@angular/material/input";
import { AthleteSideMenuComponent } from "../../../athletes/components/side-menu/side-menu";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { AppDatePipe } from "../../../common/pipes/app-date";



@Component({
  selector: 'communication-show-container',
  templateUrl: './communication-show-container.component.html',
  styleUrls: ['./communication-show-container.scss'],
  standalone: true,
  imports: [
    MatIcon,
    MatMenuModule,
    TranslateModule,
    MatIconButton,
    DateClosestPipe,
    LowerCasePipe,
    EmojifyPipe,
    NgForOf,
    MatTooltip,
    ReactiveFormsModule,
    MatFormField,
    ModuleContentComponent,
    NgIf,
    Error401Component,
    Error404Component,
    MatInput,
    AthleteSideMenuComponent,
    SideMenuComponent,
    HeaderComponent,
    PageContentComponent,
    AppDatePipe
  ],
  //changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunicationShowContainerComponent implements OnInit, AfterViewInit, OnDestroy {

  public readonly OBSERVATION_MAX_LENGTH: number = 5000;

  public pageTitle = '';
  public isLoading = true;
  public is401 = false;
  public is404 = false;
  public currentLang: string;

  public organization: Organization = {} as Organization;
  public athleteUser: User = {} as User;

  public observationForm = new FormControl('', [Validators.required]);

  public observations$: Observable<any[]> = of([]);
  public observations: any[] = [];

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(ComponentFactoryResolver) factoryResolver: ComponentFactoryResolver,
    private route: ActivatedRoute,
    private breadcrumbProvider: BreadcrumbProvider,
    private translator: TranslateService,
    private userProvider: UsersProvider,
    private communicationsProvider: CommunicationsProvider,
    private currentUserRoles: CurrentUserRolesProvider,
    private dialog: MatDialog,
    private currentUser: CurrentUser,
    private api: ApiBaseService,
    public conf: ConfigProvider,
    private title: Title,
    private changesGuard: HasPendingChangesGuard,
    private _cdr: ChangeDetectorRef,
    private ts: TranslateService,
  ) {
    this.currentLang = ts.currentLang;
  }

  @HostListener('window:beforeunload')
  public doSomething() {
    return !this.changesGuard.hasChanges();
  }

  public changeObservation(e : any) {
    this.changesGuard.registerChanges(() => this.onNewObservationClicked());
  }

  public ngOnInit(): void {
    const data$ = this.route.data.pipe(
      tap((data: any) => { //{ organization: Organization; athlete: Athlete; user: User}
        this.organization = data.organization;
        this.athleteUser = data.athlete;
        //this.user = data.user;
      }),
    );

    this.observations$ = data$.pipe(
      switchMap(data => this.communicationsProvider.getObservations(this.organization, this.athleteUser.athlete!)),
      map((observations) => {return observations.map((observation: any) => {
        observation.rights = this.checkRights(observation);
        observation.comments = observation.comments.map((comment:any) => {
          comment.rights = this.checkRights(comment);
          return comment
        })
        return observation
      })}),
      tap((observations) => {
        this.observations = observations;
        this._cdr.detectChanges();
      })
    );

    this.subscriptions.push(
      data$.subscribe(() => {
        this.isLoading = false;
        this.createPageTitle();
        this.createBreadcrumb();
      }),
      this.observations$.subscribe()
    );
  }

  public ngAfterViewInit() {}

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public onNewObservationClicked() {
    if (this.observationForm.valid) {
      const message = this.observationForm.value;
      this.communicationsProvider.saveObservation(
        this.organization,
        this.athleteUser.athlete!,
        { message: message }
      ).subscribe((observation) => {
        this.observations$.subscribe();
        this.observationForm.reset('');
        this.changesGuard.unregisterChanges();
      });
    }
  }

  public onEditObservationClicked(observation: any) {
    const dialog: MatDialogRef<CommunicationMessageDialogComponent> = this.dialog.open(CommunicationMessageDialogComponent, {
      data: {
        dialogTitle: this.translator.instant('CommunicationEdit'),
        message: observation.message
      }
    });
    dialog.afterClosed().subscribe((message) => {
      if (!message) {
        return
      }
      observation.message = message;
      this.communicationsProvider.saveObservation(
        this.organization,
        this.athleteUser.athlete!,
        observation
      ).subscribe((comment) => {
        this.observations$.subscribe();
      });
    });
  }

  public onDeleteObservationClicked(observation: any) {
    this.communicationsProvider.deleteObservation(
      this.organization,
      this.athleteUser.athlete!,
      observation
    ).subscribe(() => {
      this.observations$.subscribe();
    });
  }

  public onNewCommentClicked(observation: any) {
    const dialog: MatDialogRef<CommunicationMessageDialogComponent> = this.dialog.open(CommunicationMessageDialogComponent, {
      data: {
        dialogTitle: this.translator.instant('CommentNew'),
      }
    });
    dialog.afterClosed().subscribe((message) => {
      this.communicationsProvider.saveObservationComment(
        this.organization,
        this.athleteUser.athlete!,
        observation,
        {message: message}
      ).subscribe((observation) => {
        this.observations$.subscribe();
      });
    });
  }

  public onEditCommentClicked(observation: any, comment: any) {
    const dialog: MatDialogRef<CommunicationMessageDialogComponent> = this.dialog.open(CommunicationMessageDialogComponent, {
      data: {
        dialogTitle: this.translator.instant('CommentEdit'),
        message: comment.message
      }
    });
    dialog.afterClosed().subscribe((message) => {
      if (!message) {
        return
      }
      comment.message = message;
      this.communicationsProvider.saveObservationComment(
        this.organization,
        this.athleteUser.athlete!,
        observation,
        comment
      ).subscribe((comment) => {
        this.observations$.subscribe();
      });
    });
  }

  public onDeleteCommentClicked(observation: any, comment: any) {
    this.communicationsProvider.deleteObservationComment(
      this.organization,
      this.athleteUser.athlete!,
      observation,
      comment
    ).subscribe(() => {
      this.observations$.subscribe();
    });
  }

  private checkRights (observation: any) {
    let rights = {
      view: false,
      edit: false,
      delete: false,
    }
    if (this.currentUserRoles.have(OrganizationRole.ID_COACH, this.organization.id!)) {
      rights = {
        view: true,
        edit: observation.author.id === this.currentUser.user!.id,
        delete: observation.author.id === this.currentUser.user!.id,
      }
    }
    if (this.currentUserRoles.have(OrganizationRole.ID_SUPERCOACH, this.organization.id!)) {
      rights = {
        view: true,
        edit: observation.author.id === this.currentUser.user!.id,
        delete: true,
      }
    }
    if (this.currentUserRoles.have(OrganizationRole.ID_ADMIN, this.organization.id!)) {
      rights = {
        view: true,
        edit: true,
        delete: true,
      }
    }
    return rights;
  }

  private createPageTitle() {
    this.pageTitle = this.translator.instant('Communications');
    this.title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
  }

  private createBreadcrumb() {
    const crumbs: Crumb[] = [];
    if (!this.currentUserRoles.have(OrganizationRole.ID_ATHLETE, this.organization.id!)) {
      crumbs.push({
        shortTitle: 'Athletes',
        title: 'Athletes',
        url: '/' + this.organization!.code + '/athletes'
      });
    }
    crumbs.push({
      shortTitle: `${this.athleteUser.firstName?.substr(0, 1)}. ${this.athleteUser.lastName}`,
      title: `${this.athleteUser.firstName} ${this.athleteUser.lastName}`,
      url: `/${this.organization!.code}/athletes/${this.athleteUser.id}`
    });
    crumbs.push({
      shortTitle: 'Communication',
      title: 'Communication',
      url: `/${this.organization!.code}/athletes/${this.athleteUser.id}/communication`
    });
    this.breadcrumbProvider.setDefaultCrumbs(crumbs);
  }
}
